// src/components/admin/ImageUpload.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera, Link as LinkIcon, Star, ChevronUp, ChevronDown, Package, Expand, Plus, Minus, Move, Edit } from 'lucide-react';
import type { ImageChanges, ProductImage } from '../../types';
import { getProductImageUrl } from '../../utils/productUtilities';
import ChangeAwareInput from '../common/ChangeAwareInput';
import { getImageChangeDescription } from '../../utils/mergeChanges';
import toast from '../../utils/toast';

interface ImageUploadProps {
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
  maxImages?: number;
  variantId?: number | null;
  variantName?: string;
  disabled?: boolean;
  name?: string;
  isReviewMode?: boolean;
  previousImages?: ProductImage[];
  changeSummary?: ImageChanges | null;
  showChangeIndicators?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onChange,
  maxImages = 10,
  variantId = null,
  variantName,
  disabled = false,
  name,
  isReviewMode = false,
  previousImages = [],
  changeSummary,
  showChangeIndicators = false,
}) => {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [previewImageAltText, setPreviewImageAltText] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<ProductImage | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [altTextErrors, setAltTextErrors] = useState<Record<number, string>>({});
  // Filter images for this variant
  const variantImages = images.filter(img => {
    const imgVariant = img.product_variant ?? null;
    const targetVariant = variantId ?? null;
    return imgVariant === targetVariant;
  });
  const hasPrimaryImage = variantImages.some(img => img.is_primary === true);

  const otherImages = images.filter(img => {
    const imgVariant = img.product_variant ?? null;
    const targetVariant = variantId ?? null;
    return imgVariant !== targetVariant;
  });

  // Count actual additions (IDs in new but not in old)
  const addedCount = isReviewMode && changeSummary
    ? (changeSummary.new || []).filter(
      img => !(changeSummary.old || []).some(oldImg => oldImg.id === img.id)
    ).length
    : 0;

  // Count actual removals (IDs in old but not in new)
  const removedCount = isReviewMode && changeSummary
    ? (changeSummary.old || []).filter(
      oldImg => !(changeSummary.new || []).some(img => img.id === oldImg.id)
    ).length
    : 0;

  // Detect specific attribute changes
  const getAttributeChanges = (image: ProductImage) => {
    if (!isReviewMode || !showChangeIndicators || !changeSummary) return null;

    const imageItem = changeSummary.items?.find(
      item => item.id === image.id || item.image_url === image.image_url
    );

    if (!imageItem || !imageItem.changes) return null;

    // Return only the changes (excluding product_variant since that's shown as 'moved')
    const changes = { ...imageItem.changes };
    delete changes.product_variant; // We show this as 'Moved' badge

    return Object.keys(changes).length > 0 ? changes : null;
  };

  // Get change summary text
  const getChangeSummaryText = () => {
    const parts = [];
    if (addedCount > 0) parts.push(`${addedCount} added`);
    if (removedCount > 0) parts.push(`${removedCount} removed`);

    // Check for order changes
    const hasOrderChange = isReviewMode && changeSummary
      ? (changeSummary.new || []).some(newImg => {
        const oldImg = (changeSummary.old || []).find(old => old.id === newImg.id);
        return oldImg && (oldImg.position !== newImg.position);
      })
      : false;

    // Check for primary changes
    const hasPrimaryChange = isReviewMode && changeSummary
      ? (changeSummary.new || []).some(newImg => {
        const oldImg = (changeSummary.old || []).find(old => old.id === newImg.id);
        return oldImg && oldImg.is_primary !== newImg.is_primary;
      })
      : false;

    // Check for alt text changes
    const hasAltTextChange = isReviewMode && changeSummary
      ? (changeSummary.new || []).some(newImg => {
        const oldImg = (changeSummary.old || []).find(old => old.id === newImg.id);
        return oldImg && oldImg.alt_text !== newImg.alt_text;
      })
      : false;

    if (hasOrderChange) parts.push('order changed');
    if (hasPrimaryChange) parts.push('primary changed');
    if (hasAltTextChange) parts.push('alt text updated');

    return parts.length > 0 ? parts.join(', ') : 'No changes';
  };

  // useEffect(() => {
  //   return () => {
  //     images.forEach(img => {
  //       if (img.file && img.image_url.startsWith('blob:')) {
  //         URL.revokeObjectURL(img.image_url);
  //       }
  //     });
  //   };
  // }, []);

  // Helper to get image status from changeSummary
  const getImageStatus = (image: ProductImage): 'added' | 'removed' | 'changed' | 'variant_moved' | 'existing' | 'position_changed' | 'primary_changed' | 'alt_text_changed' | 'variant_moved' => {
    if (!isReviewMode || !showChangeIndicators || !changeSummary) return 'existing';
    // const imageItem = changeSummary.items?.find(
    //   item => item.id === image.id || item.image_url === image.image_url
    // );
    // Use _action from merged data
    const action = image._action || 'existing';
    // if (!imageItem) return 'existing';
    // Map action to status
    switch (action) {
      case 'added': return 'added';
      case 'removed': return 'removed';
      case 'variant_moved': return 'variant_moved';
      case 'primary_changed': return 'primary_changed';
      case 'position_changed': return 'position_changed';
      case 'alt_text_changed': return 'alt_text_changed';
      default: return 'existing';
    }
  };

  const getStatusStyles = (status: string) => {
    // For marking primary change and alt text changes as generic "changed"
    status = (status === 'primary_changed' || status === 'alt_text_changed') ? 'changed' : status;

    switch (status) {
      case 'added':
        return 'border-2 border-green-400';
      case 'removed':
        return 'border-2 border-red-400 opacity-60';
      case 'variant_moved':
        return 'border-2 border-purple-400';
      case 'position_changed':
        return 'border-2 border-purple-300';
      case 'changed':
        return 'border-2 border-blue-400';
      default:
        return 'border border-gray-200';
    }
  };

  // Get change summary counts
  const getChangeCounts = () => {
    if (!isReviewMode || !showChangeIndicators) {
      return { added: 0, removed: 0, changed: 0, moved: 0 };
    }

    return {
      added: variantImages.filter(img => img._action === 'added').length,
      removed: variantImages.filter(img => img._action === 'removed').length,
      changed: variantImages.filter(img =>
        img._action === 'changed' ||
        img._action === 'primary_changed' ||
        img._action === 'position_changed' ||
        img._action === 'alt_text_changed'
      ).length,
      moved: variantImages.filter(img => img._action === 'variant_moved').length
    };
  };

  const changeCounts = getChangeCounts();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    console.log('📁 Files selected:', files.length, 'for variantId:', variantId);

    if (files.length + variantImages.length > maxImages) {
      alert(`Maximum ${maxImages} images allowed for ${variantName || 'product'}`);
      return;
    }

    const newImages: ProductImage[] = files.map((file, index) => ({
      image_url: URL.createObjectURL(file), // ✅ For preview only
      file: file,                           // ✅ Keep file for upload
      is_primary: variantId === null && variantImages.length === 0 && index === 0,
      position: variantImages.length + index,
      alt_text: file.name,
      product_variant: variantId,
      _isTemp: true,                        // ✅ Mark as temporary
    }));

    const updatedImages = [...otherImages, ...variantImages, ...newImages];
    onChange(updatedImages);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUrlAdd = () => {
    if (!urlInput) return;

    if (variantImages.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed for ${variantName || 'product'}`);
      return;
    }

    try {
      new URL(urlInput);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    const newImage: ProductImage = {
      image_url: urlInput,
      is_primary: variantId === null && variantImages.length === 0,
      position: variantImages.length,
      alt_text: '',//urlInput.split('/').pop() || 'Product image',
      product_variant: variantId
    };

    const updatedImages = [...otherImages, ...variantImages, newImage];
    onChange(updatedImages);
    setUrlInput('');
    setShowUrlInput(false);
  };

  const handleSetPrimary = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation()
    if (variantId !== null) {
      alert('Only product-level images can be set as primary');
      return;
    }

    // Get the image to become primary
    const primaryImage = variantImages[index];

    // Get all variant images (excluding the primary one)
    const otherImages = variantImages.filter((_, i) => i !== index);

    // Sort other images by position
    const sortedOthers = [...otherImages].sort((a, b) => (a.position || 0) - (b.position || 0));

    // Rebuild array: primary first, then others with positions shifted
    const reorderedVariantImages = [
      { ...primaryImage, position: 0, is_primary: true },
      ...sortedOthers.map((img, i) => ({
        ...img,
        position: i + 1,
        is_primary: false
      }))
    ];

    // Find actual indices in the full images array
    const actualIndices = variantImages.map(img =>
      images.findIndex(i => i.image_url === img.image_url && i.product_variant === variantId)
    );

    // Update the full images array with reordered variant images
    const updated = [...images];
    actualIndices.forEach((idx, i) => {
      updated[idx] = reorderedVariantImages[i];
    });

    onChange(updated);
  };

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation()
    const actualIndex = images.findIndex(img =>
      img.product_variant === variantId &&
      variantImages[index].image_url === img.image_url
    );

    const updated = images.filter((_, i) => i !== actualIndex);

    if (variantId === null && images[actualIndex].is_primary) {
      const productImages = updated.filter(img => img.product_variant === null);
      if (productImages.length > 0) {
        const firstProductIndex = updated.findIndex(img => img.product_variant === null);
        updated[firstProductIndex].is_primary = true;
      }
    }

    // Re-sort remaining images by current position
    const sortedRemaining = [...updated].sort((a, b) => (a.position || 0) - (b.position || 0));

    // Reassign positions sequentially
    const reassigned = sortedRemaining.map((img, i) => ({
      ...img,
      position: i
    }));

    onChange(reassigned);
  };

  const handleAltTextChange = (index: number, altText: string) => {
    const actualIndex = images.findIndex(img =>
      img.product_variant === variantId &&
      variantImages[index].image_url === img.image_url
    );

    // Validate alt text
    if (altText.length > 255) {
      setAltTextErrors(prev => ({ ...prev, [index]: 'Alt text must be 255 characters or less' }));
      return;
    }
    setAltTextErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });

    const updated = [...images];
    updated[actualIndex].alt_text = altText;
    onChange(updated);
  };

  const moveImage = (e: React.MouseEvent<HTMLButtonElement>, index: number, direction: 'up' | 'down') => {
    e.stopPropagation()
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === variantImages.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;

    // Get all images for this variant
    const variantIndices = variantImages.map(img =>
      images.findIndex(i => i.image_url === img.image_url && i.product_variant === variantId)
    );

    // Create reordered array with updated positions
    const reorderedImages = variantIndices.map((idx, i) => ({
      ...images[idx],
      position: i
    }));

    // Swap the two images in the reordered array
    const temp = reorderedImages[index];
    reorderedImages[index] = reorderedImages[newIndex];
    reorderedImages[newIndex] = temp;

    // Update positions after swap to maintain correct order
    const finalVariantImages = reorderedImages.map((img, i) => ({
      ...img,
      position: i
    }));

    // Create the final updated array
    const updated = [...images];
    variantIndices.forEach((idx, i) => {
      updated[idx] = finalVariantImages[i];
    });

    onChange(updated);
  };

  // Helper to get alt text change info for an image
  const getAltTextChangeInfo = (image: ProductImage, index: number) => {
    if (!isReviewMode || !changeSummary) {
      return { value: image.alt_text || '', previous: '', hasChanged: false };
    }

    const imageItem = changeSummary.items?.find(
      item => item.id === image.id || item.image_url === image.image_url
    );

    if (!imageItem) {
      return { value: image.alt_text || '', previous: '', hasChanged: false };
    }

    // Check if alt_text changed
    if (imageItem.changes?.alt_text) {
      return {
        value: imageItem.changes.alt_text.new,
        previous: imageItem.changes.alt_text.old,
        hasChanged: true
      };
    }

    return {
      value: image.alt_text || '',
      previous: '',
      hasChanged: false
    };
  };
  const getImageChangeSummary = () => {
    if (!isReviewMode || !changeSummary?.images) return null;

    const { summary } = changeSummary.images;
    const parts = [];

    if (summary.added > 0) parts.push(`${summary.added} added`);
    if (summary.removed > 0) parts.push(`${summary.removed} removed`);
    if (summary.changed > 0) parts.push(`${summary.changed} modified`);
    if (summary.variant_changed > 0) parts.push(`${summary.variant_changed} moved between variants`);

    return parts.length > 0 ? parts.join(', ') : 'No changes';
  };

  // Sort variant images by position
  const sortedVariantImages = [...variantImages].sort((a, b) => {
    const statusA = getImageStatus(a);
    const statusB = getImageStatus(b);

    // If one is removed and the other isn't, removed goes to the end
    if (statusA === 'removed' && statusB !== 'removed') return 1;
    if (statusB === 'removed' && statusA !== 'removed') return -1;

    // Otherwise sort by position
    const posA = Number(a.position) ?? 0;
    const posB = Number(b.position) ?? 0;

    return posA - posB;
  });


  return (
    <div className="space-y-4" data-field={name}>
      {/* Header with status summary */}
      {isReviewMode && showChangeIndicators && changeSummary && (
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-500">Legend:</span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-green-100 border border-green-300 rounded-sm" />
            <span className="text-gray-600">Added</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm" />
            <span className="text-gray-600">Removed</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded-sm" />
            <span className="text-gray-600">Changed</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-purple-100 border border-purple-300 rounded-sm" />
            <span className="text-gray-600">Moved</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-blue-50 border border-gray-200 rounded-sm" />
            <span className="text-gray-600">Existing</span>
          </span>

          <div className="flex gap-3 ml-2">
            {changeCounts.added > 0 && (
              <span className="text-green-600">+{changeCounts.added} added</span>
            )}
            {changeCounts.removed > 0 && (
              <span className="text-red-600">-{changeCounts.removed} removed</span>
            )}
            {changeCounts.changed > 0 && (
              <span className="text-blue-600">{changeCounts.changed} modified</span>
            )}
            {changeCounts.moved > 0 && (
              <span className="text-purple-600">{changeCounts.moved} moved</span>
            )}
          </div>
        </div>
      )}

      {variantName && (
        <div className="flex items-center gap-2 mb-2">
          <Package size={16} className="text-gray-500" />
          <h4 className="text-sm font-medium text-gray-700">{variantName}</h4>
          <span className="text-xs text-gray-500">({variantImages.length}/{maxImages} images)</span>
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {sortedVariantImages.map((image, index) => {
          const status = getImageStatus(image);
          const statusStyles = getStatusStyles(status);
          const attributeChanges = getAttributeChanges(image);
          const altTextInfo = getAltTextChangeInfo(image, index);

          return (
            <div
              key={`${variantId}-${index}-${image.id || image.image_url}`}
              className={`relative rounded-lg p-2 ${statusStyles} ${status === 'removed' ? 'opacity-60' : ''}`}
            >
              {/* Image container with its own group */}
              <div className="relative group">
                {/* Image */}
                <div
                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer relative"
                  onClick={() => setPreviewImage(image)}
                >
                  <img
                    src={getProductImageUrl(image.image_url)}
                    alt={image.alt_text || 'Product image'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      setImageErrors(prev => ({ ...prev, [image.image_url]: true }));
                    }}
                  />

                  {/* Overlay - shows only on the image */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-2">
                      {/* Move all action buttons here */}
                      {!isReviewMode && (
                        <>
                          {
                            !isReviewMode &&
                            <div className="absolute top-4 right-4 flex gap-1">
                              {index > 0 && (
                                <button
                                  onClick={(e) => moveImage(e, index, 'up')}
                                  className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
                                  title="Move up"
                                  type="button"
                                  disabled={disabled || isReviewMode || (index === 1 && hasPrimaryImage)}
                                >
                                  <ChevronUp size={14} />
                                </button>
                              )}
                              {index < variantImages.length - 1 && !image.is_primary && (
                                <button
                                  onClick={(e) => moveImage(e, index, 'down')}
                                  className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
                                  title="Move down"
                                  type="button"
                                  disabled={disabled || isReviewMode}
                                >
                                  <ChevronDown size={14} />
                                </button>
                              )}
                            </div>
                          }
                        </>
                      )}
                      {variantId === null && !image.is_primary && !isReviewMode && (
                        <button
                          onClick={(e) => handleSetPrimary(e, index)}
                          className="p-2 bg-white rounded-full hover:bg-yellow-100"
                          title="Set as primary"
                          type="button"
                          disabled={disabled}
                        >
                          <Star size={16} className="text-yellow-600" />
                        </button>
                      )}
                      {!disabled && !isReviewMode && (
                        <button
                          onClick={(e) => handleRemove(e, index)}
                          className="p-2 bg-white rounded-full hover:bg-red-100"
                          title="Remove"
                          type="button"
                        >
                          <X size={16} className="text-red-600" />
                        </button>
                      )}
                      <button
                        onClick={() => setPreviewImage(image)}
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                        title="View full size"
                        type="button"
                      >
                        <Expand size={16} className="text-gray-600" />
                      </button>
                    </div>
                  </div>
                  {/* Badge Container - Top Left */}
                  <div className="absolute top-2 left-2 flex flex-col items-start gap-1">

                    {/* PRIMARY Badge - below action badge if present */}
                    {variantId === null && image.is_primary && (
                      <div className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full flex items-center">
                        <Star size={12} className="mr-1 fill-current" /> PRIMARY
                      </div>
                    )}

                    {/* Badge Container - Top Left */}
                    <div className="">
                      {/* Attribute Change Indicators - shows all changes for this image */}
                      {isReviewMode && showChangeIndicators && (
                        <div className="flex flex-col gap-0.5">
                          {/* Added/Removed/Moved status */}
                          {status === 'added' && (
                            <div className="bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Plus size={10} /> Added
                            </div>
                          )}
                          {status === 'removed' && (
                            <div className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Minus size={10} /> Removed
                            </div>
                          )}
                          {status === 'variant_moved' && (
                            <div className="bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Move size={10} /> Moved
                            </div>
                          )}

                          {/* Attribute changes - only for existing images */}
                          {status === 'changed' && attributeChanges.isPrimary && (
                            <div className="bg-blue-100 text-blue-800 text-[10px] px-1.5 py-0.5 rounded">
                              {attributeChanges.isPrimary.new ? '→ Primary' : '← Primary'}
                            </div>
                          )}
                          {status === 'position_changed' && (
                            <div className="bg-purple-100 text-purple-800 text-[10px] px-1.5 py-0.5 rounded">
                              Position: {attributeChanges.position.old + 1} → {attributeChanges.position.new + 1}
                            </div>
                          )}
                          {status === 'alt_text_changed' && (
                            <div className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0.5 rounded">
                              Alt text updated
                            </div>
                          )}
                          {status === 'primary_changed' && (
                            <div className="bg-blue-100 text-blue-800 text-[10px] px-1.5 py-0.5 rounded">
                              {image.is_primary ? '→ Primary' : '← Primary'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              {/* Alt Text Input - Using ChangeAwareInput */}
              <div className="mt-2 relative group">
                <ChangeAwareInput
                  label="Alt Text"
                  name={`alt_text_${image.id || index}`}
                  type="text"
                  value={altTextInfo.value}
                  previousValue={altTextInfo.previous}
                  showChange={altTextInfo.hasChanged}
                  onChange={(e) => handleAltTextChange(index, e.target.value)}
                  readOnly={isReviewMode || disabled}
                  error={altTextErrors[index]}
                  className={`w-full text-xs px-2 py-1 border rounded max-h-20 overflow-y-auto ${altTextErrors[index] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    } ${isReviewMode ? 'bg-gray-50' : ''}`}
                  placeholder="Enter alt text for accessibility"
                />

                {/* Tooltip - only shows when hovering the input */}
                {(altTextInfo.value?.length > 50 || altTextInfo.previous?.length > 50) && (
                  <div className="absolute z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 delay-300 bg-gray-100 border border-gray-200 text-gray-800 text-xs rounded-lg p-3 max-w-xs whitespace-normal break-words -top-2 left-0 transform -translate-y-full shadow-lg">
                    {/* Arrow */}
                    <div className="absolute -bottom-1.5 left-4 w-3 h-3 bg-gray-100 border-r border-b border-gray-200 transform rotate-45"></div>

                    {altTextInfo.hasChanged && altTextInfo.previous && (
                      <div className="mb-1.5 pb-1.5 border-b border-gray-100">
                        <span className="text-gray-500 font-medium">Old:</span>
                        <span className="text-gray-600 ml-1">{altTextInfo.previous}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500 font-medium">{altTextInfo.hasChanged ? 'New:' : 'Current:'}</span>
                      <span className="text-gray-800 ml-1">{altTextInfo.value}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Add Image Placeholder - Hidden in review mode */}
        {!isReviewMode && variantImages.length < maxImages && !disabled && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center aspect-square hover:border-blue-500 transition-colors">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center text-gray-500 hover:text-blue-600"
              type="button"
            >
              <Camera size={24} />
              <span className="text-xs mt-1">Upload</span>
            </button>
            <button
              onClick={() => setShowUrlInput(true)}
              className="flex flex-col items-center text-gray-500 hover:text-blue-600 mt-2"
              type="button"
            >
              <LinkIcon size={24} />
              <span className="text-xs mt-1">Add URL</span>
            </button>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* URL Input Modal */}
      {showUrlInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">
              Add Image URL for {variantName || 'Product'}
            </h3>
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-3 py-2 border rounded-lg mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowUrlInput(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleUrlAdd}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                type="button"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            {/* Image */}
            <img
              src={getProductImageUrl(previewImage)}
              alt={previewImage.alt_text || "Product preview"}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />

            {/* Alt Text Display - Clean pill style */}
            {previewImage.alt_text && (
              <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full border border-white/10 max-w-2xl">
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Alt:</span>
                <span className="text-sm text-white/90 truncate">{previewImage.alt_text}</span>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;